// src/pages/Projects.jsx

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaArrowRight,
  FaExternalLinkAlt,
  FaGooglePlay,
  FaRocket,
} from "react-icons/fa";

import snackhub from "../assets/projects/snack.png";
import udaykiran from "../assets/projects/udaykiran.png";
import jyothishyam from "../assets/projects/jyothishyam.png";
import referx from "../assets/projects/referx.png";
import lootbazzare from "../assets/projects/lootbazzar.png";
import mkTours from "../assets/projects/mk-tours.png";

import linktre21 from "../assets/projects/linktre21.png";
import magicbites from "../assets/projects/magicbites.webp";

/* ============================================================
   WEBSITE PROJECTS
============================================================ */

const WEBSITE_PROJECTS = [
  {
    number: "01",
    title: "SnackHub",
    category: "Food & Restaurant",
    description:
      "A modern website created for a food brand to showcase its offerings and create an engaging digital experience.",
    image: snackhub,
    url: "https://snackhub.in/",
  },

  {
    number: "02",
    title: "Uday Kiran Portfolio",
    category: "Personal Portfolio",
    description:
      "A professional portfolio website designed to showcase personal branding, skills, experience and creative work.",
    image: udaykiran,
    url: "https://udaykiranportfolio.web.app/",
  },


  {
    number: "03",
    title: "Sri Venkataramana Jyothishyam",
    category: "Professional Services",
    description:
      "A clean and trustworthy business website designed for a specialized professional service.",
    image: jyothishyam,
    url: "https://www.srivenkataramanajyothishyam.in/",
  },

  {
    number: "04",
    title: "ReferX",
    category: "Web Platform",
    description:
      "A focused digital platform built around referrals, connections and a simple user experience.",
    image: referx,
    url: "http://referx.in/",
  },

  {
    number: "05",
    title: "Loot Bazzare",
    category: "E-Commerce",
    description:
      "A shopping-focused web experience designed to present products and create an easy online browsing journey.",
    image: lootbazzare,
    url: "https://lootbazzare.vercel.app/",
  },

  {
    number: "06",
    title: "MK Tours & Travel",
    category: "Travel & Tourism",
    description:
      "A travel website created to showcase destinations, services and tourism offerings in an engaging way.",
    image: mkTours,
    url: "https://mktoursandtravel.in/",
  },


  {
    number: "07",
    title: "LinkTre21",
    category: "Web Platform",
    description:
      "A simple digital platform designed to organize and share multiple links in one convenient experience.",
    image: linktre21,
    url: "https://linktre21.web.app/",
  },
];

/* ============================================================
   MOBILE APP PROJECTS
============================================================ */

const APP_PROJECTS = [
  {
    number: "01",
    title: "MagicBites",
    category: "Instant Delivery App",
    description:
      "A mobile delivery application built for customers to discover and order food, groceries, medicines and everyday essentials.",
    image: magicbites,
    url: "https://play.google.com/store/apps/details?id=com.magicbites.userapp",
  },
];

/* ============================================================
   WEBSITE PROJECT CARD
============================================================ */

function WebsiteProjectCard({ project, index }) {
  return (
    <article
      data-aos="fade-up"
      data-aos-delay={index * 60}
      className="group overflow-hidden rounded-[28px] border border-slate-200 bg-white p-3 shadow-[0_10px_45px_rgba(15,23,42,0.06)] transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-[0_25px_65px_rgba(37,99,235,0.12)]"
    >
      {/* ==================================================
          IMAGE
      ================================================== */}

      <div className="relative h-[230px] overflow-hidden rounded-[22px] bg-slate-100 sm:h-[250px]">
        <img
          src={project.image}
          alt={`${project.title} website preview`}
          loading="lazy"
          className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-[1.035]"
        />

        {/* Image overlay */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent opacity-70" />

        {/* Project number */}
        <div className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-xl border border-white/30 bg-slate-950/40 text-[10px] font-extrabold text-white backdrop-blur-md">
          {project.number}
        </div>

        {/* Category */}
        <div className="absolute bottom-4 left-4">
          <span className="rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-[9px] font-extrabold uppercase tracking-wider text-white backdrop-blur-md">
            {project.category}
          </span>
        </div>
      </div>

      {/* ==================================================
          CONTENT
      ================================================== */}

      <div className="p-4 pb-5 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-blue-600">
              Website Project
            </p>

            <h3 className="mt-2 text-xl font-extrabold tracking-tight text-slate-950 transition duration-300 group-hover:text-blue-600">
              {project.title}
            </h3>
          </div>

          <span className="text-xs font-bold text-slate-300">
            {project.number}
          </span>
        </div>

        <p className="mt-3 text-sm leading-6 text-slate-500">
          {project.description}
        </p>

        {/* Live Website */}
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group/button mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-xs font-extrabold text-white shadow-md shadow-blue-600/15 transition duration-300 hover:-translate-y-0.5 hover:bg-blue-700 hover:shadow-lg"
        >
          Visit Live Website

          <FaExternalLinkAlt className="text-[9px] transition duration-300 group-hover/button:translate-x-0.5" />
        </a>
      </div>
    </article>
  );
}

/* ============================================================
   MOBILE APP CARD
============================================================ */

function AppProjectCard({ project }) {
  return (
    <article
      data-aos="fade-up"
      className="overflow-hidden rounded-[32px] border border-blue-100 bg-white shadow-[0_20px_70px_rgba(37,99,235,0.08)]"
    >
      <div className="grid lg:grid-cols-[1fr_1fr]">
        {/* ==================================================
            APP IMAGE
        ================================================== */}

        <div className="relative min-h-[350px] overflow-hidden bg-slate-100 lg:min-h-[500px]">
          <img
            src={project.image}
            alt={`${project.title} mobile app preview`}
            loading="lazy"
            className="h-full w-full object-cover object-top transition duration-700 hover:scale-[1.02]"
          />

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />

          <div className="absolute left-6 top-6">
            <span className="rounded-full border border-white/25 bg-slate-950/40 px-4 py-2 text-[10px] font-extrabold uppercase tracking-wider text-white backdrop-blur-md">
              MOBILE APP
            </span>
          </div>
        </div>

        {/* ==================================================
            APP CONTENT
        ================================================== */}

        <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-600">
            APP PROJECT · {project.number}
          </p>

          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
            {project.title}
          </h2>

          <p className="mt-5 max-w-xl text-base leading-7 text-slate-500 sm:text-lg">
            {project.description}
          </p>

          {/* Features */}
          <div className="mt-7 grid gap-3 sm:grid-cols-2">
            {[
              "Customer mobile application",
              "Online ordering experience",
              "Multiple product categories",
              "Fast delivery experience",
            ].map((item) => (
              <div
                key={item}
                className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600"
              >
                <span className="mr-2 text-blue-600">✓</span>
                {item}
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-6 py-3.5 text-sm font-extrabold text-white shadow-lg transition duration-300 hover:-translate-y-1 hover:bg-blue-600"
            >
              <FaGooglePlay />

              View on Google Play

              <FaArrowRight className="text-[10px] transition group-hover:translate-x-1" />
            </a>

            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-6 py-3.5 text-sm font-extrabold text-blue-600 transition duration-300 hover:-translate-y-1 hover:bg-blue-100"
            >
              Build Your App
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ============================================================
   MAIN PAGE
============================================================ */

export default function Projects() {
  useEffect(() => {
    AOS.init({
      duration: 750,
      once: true,
      easing: "ease-out-cubic",
      offset: 70,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <main className="min-h-screen overflow-hidden bg-white font-[Manrope] text-slate-900">
      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="relative overflow-hidden px-5 pb-16 pt-32 sm:px-8 lg:px-10 lg:pb-20">
        {/* Background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-blue-100/60 blur-3xl" />

          <div className="absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-cyan-100/50 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(#2563eb 1px, transparent 1px), linear-gradient(90deg, #2563eb 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />
        </div>

        <div
          className="relative mx-auto max-w-5xl text-center"
          data-aos="fade-up"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-5 py-2.5 text-xs font-extrabold tracking-[0.2em] text-blue-600 shadow-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
            </span>

            OUR PROJECTS
          </span>

          <h1 className="mx-auto mt-7 max-w-4xl text-4xl font-extrabold leading-[1.08] tracking-[-0.04em] text-slate-950 sm:text-5xl lg:text-6xl">
            Work We've
            <span className="mt-2 block bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 bg-clip-text text-transparent">
              Built For Businesses.
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-slate-500 sm:text-lg">
            Explore websites and mobile applications we've designed and
            developed for businesses, brands, entrepreneurs and real-world
            customers.
          </p>

          {/* Stats */}
          <div className="mx-auto mt-10 grid max-w-xl grid-cols-3 divide-x divide-slate-200 rounded-2xl border border-slate-200 bg-white/80 py-4 shadow-sm backdrop-blur-sm">
            <div>
              <p className="text-2xl font-extrabold text-slate-950">
                07
              </p>

              <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Websites
              </p>
            </div>

            <div>
              <p className="text-2xl font-extrabold text-slate-950">
                01
              </p>

              <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Mobile App
              </p>
            </div>

            <div>
              <p className="text-2xl font-extrabold text-slate-950">
                8
              </p>

              <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Projects
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          WEBSITE PROJECTS
      ====================================================== */}

      <section
        id="web-projects"
        className="relative border-y border-slate-100 bg-slate-50 px-5 py-20 sm:px-8 lg:px-10"
      >
        <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-blue-100/50 blur-3xl" />

        <div className="relative mx-auto max-w-6xl">
          {/* Section heading */}
          <div
            data-aos="fade-up"
            className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end"
          >
            <div className="max-w-2xl">
              <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-600">
                WEBSITE PROJECTS
              </span>

              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
                Websites We've Built.
              </h2>

              <p className="mt-4 text-base leading-7 text-slate-500 sm:text-lg">
                Real websites created for different industries, business
                goals and customer experiences.
              </p>
            </div>

            <div className="rounded-full border border-blue-100 bg-white px-4 py-2 text-xs font-extrabold text-blue-600 shadow-sm">
              {WEBSITE_PROJECTS.length} Live Websites
            </div>
          </div>

          {/* Project Grid */}
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {WEBSITE_PROJECTS.map((project, index) => (
              <WebsiteProjectCard
                key={project.title}
                project={project}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          MOBILE APP PROJECTS
      ====================================================== */}

      <section className="px-5 py-20 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-6xl">
          {/* Heading */}
          <div
            data-aos="fade-up"
            className="mx-auto max-w-3xl text-center"
          >
            <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-600">
              MOBILE APP PROJECTS
            </span>

            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-slate-950 sm:text-4xl">
              Apps Built For Real Users.
            </h2>

            <p className="mt-4 text-base leading-7 text-slate-500 sm:text-lg">
              Mobile applications designed to make everyday services simpler,
              faster and more accessible.
            </p>
          </div>

          {/* App Project */}
          <div className="mt-12 space-y-6">
            {APP_PROJECTS.map((project) => (
              <AppProjectCard
                key={project.title}
                project={project}
              />
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="px-5 pb-20 pt-4 sm:px-8 lg:px-10">
        <div
          data-aos="fade-up"
          className="relative mx-auto max-w-6xl overflow-hidden rounded-[32px] bg-gradient-to-br from-blue-600 via-blue-600 to-cyan-500 px-6 py-14 text-center shadow-2xl shadow-blue-600/20 sm:px-10 sm:py-16"
        >
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

          <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-cyan-300/20 blur-3xl" />

          <div className="relative mx-auto max-w-3xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-xl text-white backdrop-blur-md">
              <FaRocket />
            </div>

            <p className="mt-6 text-xs font-extrabold tracking-[0.22em] text-blue-100">
              READY TO BUILD?
            </p>

            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Your Project Could Be Next.
            </h2>

            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-blue-100 sm:text-lg">
              Have an idea for a website, mobile app or digital product? Let's
              turn it into something real.
            </p>

            <Link
              to="/contact"
              className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-sm font-extrabold text-blue-600 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-xl"
            >
              Start Your Project

              <FaArrowRight className="text-[10px] transition group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}